/**
 * Messier (Messier & Associés) brand style configuration — colors, typography,
 * and prompt rules extracted from the client's "Messier.pptx" (slide master
 * colorScheme "Messier & Associés V2" + fontScheme, layout placeholder
 * geometry, and real template charts, via the OOXML).
 *
 * Mirrors the shape of `evercoreStyleConfig.ts` / `safraStyleConfig.ts`. When
 * Messier style is active the prompt below is appended to the system prompt and
 * the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master colorScheme; fonts from fontScheme
 * (Arial for titles AND body). Slide size is A4-style 780×540 pt. Title/content
 * geometry is from the slide master. The logo lives on the slide master(s), so
 * default AddSlide inherits it. Template charts lead with navy (theme tx2 /
 * dk2), which drives the series palette below.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Messier & Associés V2" scheme
// ---------------------------------------------------------------------------

export const MESSIER_COLORS = {
  navy: "002060", // dark2 — Messier navy (primary brand / chart lead)
  lightBlue: "8EB4E3", // light2
  rust: "6E321A", // accent1
  teal: "375C56", // accent2 — dark teal green
  gold: "9C7C36", // accent3
  sage: "A5BFA7", // accent4
  yellow: "EEC222", // accent5
  purple: "4F3D93", // accent6

  ink: "000000", // dark1 — titles & body text (black)
  white: "FFFFFF", // light1

  ruleGray: "808080", // neutral gray for rule / separator lines
  altRow: "F2F2F2", // light neutral for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Messier style is active. Values map 1:1 to
 * the Messier slide-master colorScheme + fontScheme.
 */
export const MESSIER_THEME_COLORS: ThemeColors = {
  dark1: MESSIER_COLORS.ink, // #000000 — Text1
  dark2: MESSIER_COLORS.navy, // #002060 — Text2
  light1: MESSIER_COLORS.white, // #FFFFFF — Background1
  light2: MESSIER_COLORS.lightBlue, // #8EB4E3 — Background2
  accent1: MESSIER_COLORS.rust, // #6E321A
  accent2: MESSIER_COLORS.teal, // #375C56
  accent3: MESSIER_COLORS.gold, // #9C7C36
  accent4: MESSIER_COLORS.sage, // #A5BFA7
  accent5: MESSIER_COLORS.yellow, // #EEC222
  accent6: MESSIER_COLORS.purple, // #4F3D93
  hyperlink: "0000FF",
  followedHyperlink: "800080",
  majorFont: "Arial",
  minorFont: "Arial",
};

/**
 * Ordered series palette. Template charts lead with navy (theme tx2 / dk2), so
 * navy is first, followed by distinct theme accents for readable multi-series
 * separation.
 */
export const MESSIER_CHART_SERIES_PALETTE = [
  MESSIER_COLORS.navy,
  MESSIER_COLORS.rust,
  MESSIER_COLORS.teal,
  MESSIER_COLORS.gold,
  MESSIER_COLORS.purple,
  MESSIER_COLORS.yellow,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the Messier master)
// ---------------------------------------------------------------------------

export const MESSIER_SLIDE_DIMENSIONS = { width: 780, height: 540 } as const;

export const MESSIER_POSITIONS = {
  title: { left: 20.1, top: 11.3, width: 739.8, height: 28.3 },
  contentStart: { left: 20.1, top: 117.0 },
  footnote: { left: 20.1, top: 522.4, width: 739.8, height: 17.6 },
} as const;

// Authoritative layout names from the Messier master.
export const MESSIER_LAYOUT_NAMES = [
  "Title Slide (1)",
  "Title Slide (2)",
  "Title Slide (3)",
  "Title and Content",
  "Title and Content + numbering",
  "Two Lines",
  "Two Lines + numbering",
  "Four Content",
  "Four Content + numbering",
  "Table of Contents",
  "Divider",
  "Table of content Storage Layout",
  "Section Divider Storage Layout",
  "SubSection Divider Storage Layout",
  "Reminder shapes Storage Layout",
  "UpSlide Breadcrumb (Disabled)",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Messier style is active
// ---------------------------------------------------------------------------

export const MESSIER_STYLE_PROMPT = `

## MESSIER BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Messier uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Arial"\`
NEVER omit fontFamily when Messier style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the Messier master colorScheme "Messier & Associés V2")
Use ONLY these Messier brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${MESSIER_COLORS.ink}
- Text2 / dark2: Navy #${MESSIER_COLORS.navy}
- Background1 / light1: White #${MESSIER_COLORS.white}
- Background2 / light2: Light blue #${MESSIER_COLORS.lightBlue}
- Accent1: Rust #${MESSIER_COLORS.rust}
- Accent2: Teal #${MESSIER_COLORS.teal}
- Accent3: Gold #${MESSIER_COLORS.gold}
- Accent4: Sage #${MESSIER_COLORS.sage}
- Accent5: Yellow #${MESSIER_COLORS.yellow}
- Accent6: Purple #${MESSIER_COLORS.purple}

**Usage:**
- **Primary brand / emphasis**: Navy #${MESSIER_COLORS.navy}
- **Text / ink**: Black #${MESSIER_COLORS.ink}
- **Lines & borders**: Gray #${MESSIER_COLORS.ruleGray}
- **Background / shading**: Light gray #${MESSIER_COLORS.altRow}
- **Chart series palette** (in order): ${MESSIER_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${MESSIER_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from the Messier master)
Slide dimensions: 780 × 540 pt (A4-style).

**Placeholder positions:**
- **Title**: left=20.1, top=11.3, width=739.8, height=28.3 — "Arial" 18pt, navy (#${MESSIER_COLORS.navy}) or black, left-aligned
- **Content area**: starts at left=20.1, top=117.0
- **Footer / page number**: bottom band around top=522 — "Arial" ~9pt, gray

**Content text hierarchy (from the master — "Arial", black #${MESSIER_COLORS.ink}, ~10pt body):**
- Keep body text ~10pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold or the navy accent; bullets otherwise inherit black text.

### Charts (Messier style)
- Font: "Arial", minimum 8pt
- **Primary data color**: navy (#${MESSIER_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Messier style)
- Header row: navy (#${MESSIER_COLORS.navy}) fill with white text, bold
- Body: white fill, black "Arial" text (10pt)
- Alternate-row shading: light gray (#${MESSIER_COLORS.altRow})
- Borders: thin gray (#${MESSIER_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Messier style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${MESSIER_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid gray (#${MESSIER_COLORS.ruleGray}) lines
- Page numbers / footer: ~9pt, gray, "Arial"

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
